import { type ConfigOriginKey } from '@/lib/config-origin/domain';
import {
	CASH_OPTION,
	FINANCE_CALCULATOR_CASH_INPUT_VALUES,
	FINANCE_CALCULATOR_INPUTS_PARAM,
	FINANCE_CALCULATOR_INPUTS_VARIABLE
} from '@/lib/price/tfs/utils/constants';
import {
	SELECTION_PARAM_INSURANCE,
	SELECTION_PARAM_INSURANCE_QUOTE,
	SELECTION_PARAM_TRADE_IN,
	SELECTION_PARAM_WALLBOX
} from '@/constants/selection_params';
import { type BasketQuoteItem, type BasketData } from '@/lib/order/domain/basket';
import { type OrderItem, type Order } from '@/lib/order/domain/order';
import { type FinanceQuote, type InsuranceQuote } from '@/lib/order/domain/sharedSchemas';
import { getCarParams } from '@/lib/order/utils/getCarParams';
import { encodeInputValue } from '@/lib/price/tfs/utils/defaultProducts/financeProductAndOptionsUtils';
import { PROTECTED_VALUES_PARAMETER } from '@/lib/protected-param-values/constants';
import { type SelectedVehicle } from '@/providers/car-store-provider';

export function orderOrBasketToReturnParams(
	origin: ConfigOriginKey,
	orderOrBasket: Order | BasketData,
	selectedVehicle: SelectedVehicle,
	protectedValues?: string
): string {
	let params: Record<string, string> = {};
	let orderOrBasketItem: OrderItem | BasketQuoteItem | undefined;

	// MARK: Customer Interests Inputs
	if (orderOrBasket.customerInterests?.wallBox) {
		params[SELECTION_PARAM_WALLBOX] = 'true';
	}

	if (orderOrBasket.customerInterests?.tradeIn) {
		params[SELECTION_PARAM_TRADE_IN] = 'true';
	}

	// Extract insurance and finance quote based on input type
	let insuranceQuote: InsuranceQuote | undefined | null;
	let financeQuote: FinanceQuote | undefined | null;

	if (isOrder(orderOrBasket)) {
		orderOrBasketItem = orderOrBasket.orderItems[0];
		insuranceQuote = orderOrBasket.vehicleInsuranceQuote;
		financeQuote = orderOrBasket.financeQuote;
	} else {
		orderOrBasketItem = orderOrBasket.quoteItems[0];
		insuranceQuote = orderOrBasketItem.vehicleInsuranceQuote;
		financeQuote = orderOrBasketItem.financeQuote;
	}

	// MARK: Car part Inputs
	const carParams: Record<string, string> = getCarParams(origin, orderOrBasketItem, selectedVehicle);
	params = { ...params, ...carParams };

	// MARK: Insurance Inputs
	if (insuranceQuote?.insuranceProduct) {
		params[SELECTION_PARAM_INSURANCE] = 'true';
		if (insuranceQuote.id) {
			params[SELECTION_PARAM_INSURANCE_QUOTE] = insuranceQuote.id;
		}
	}

	// MARK: Finance Inputs
	if (financeQuote && financeQuote.financeProduct !== CASH_OPTION) {
		const inputValues = financeQuote.quotationArray?.find((quotation) => quotation.variable === FINANCE_CALCULATOR_INPUTS_VARIABLE);
		if (inputValues) {
			params[FINANCE_CALCULATOR_INPUTS_PARAM] = inputValues.value;
		}
	} else {
		params[FINANCE_CALCULATOR_INPUTS_PARAM] = encodeInputValue(FINANCE_CALCULATOR_CASH_INPUT_VALUES);
	}

	// MARK: Protected Values Input
	if (protectedValues) {
		params[PROTECTED_VALUES_PARAMETER] = protectedValues;
	}

	return new URLSearchParams(params).toString();
}

function isOrder(input: Order | BasketData): input is Order {
	return 'orderItems' in input && 'customer' in input;
}
